import NumberInput from '../../NumberInput';
import { Fragment, useEffect, useMemo, useState } from 'react';

import SearchableList from '../../SearchableList';
import { Button } from 'react-bootstrap';
import Weight, { WeightUnit } from './Weight';
import Currencies from './Currencies';
import CurrencyText from './CurrencyText';
import { CurrencyCode } from './Currency';

type CurrencyAmounts = Record<CurrencyCode, number>;

type TableInventoryProps<Item, SearchableItem> = {
    className?: string;
    searchableItems: SearchableItem[];
    thead: React.ReactNode;

    items: Item[];
    setItems: (items: Item[]) => void;
    sorted?: boolean;
    sortedSearchable?: boolean;
    showSearchableList?: boolean;

    renderItem: (item: Item, isEditing: boolean) => React.ReactNode;
    renderDetails?: (item: Item, isEditing: boolean) => React.ReactNode;
    getDescription?: (item: Item) => React.ReactNode;
    onNotesChange?: (item: Item, notes: string) => void;
    getWeight?: (item: Item) => number;
    weightUnit?: WeightUnit;
    onWeightChange?: (item: Item, weight: number) => void;
    canEditWeight?: (item: Item) => boolean;
    getValue?: (item: Item) => CurrencyAmounts;
    onValueChange?: (item: Item, value: CurrencyAmounts) => void;
    canEditValue?: (item: Item) => boolean;
    columnCount?: number;
    mobileColumnCount?: number;
    emptyMessage?: React.ReactNode;

    onSelect: (item: SearchableItem) => void;
    onSave?: (item: Item) => Promise<boolean> | boolean;
    onDelete?: (item: Item) => Promise<boolean> | boolean;
    canSave?: (item: Item) => boolean;
    isUnsavedItem?: (item: Item) => boolean;
    getKey: (item: Item | SearchableItem) => string;
    getLabel: (item: Item | SearchableItem) => string;
    newItem?: () => Item;
}

function TableInventory<Item, SearchableItem>({
    className = '',
    searchableItems,
    thead,
    items,
    onSelect,
    setItems,
    sorted = false,
    sortedSearchable = false,
    showSearchableList = true,
    renderItem,
    renderDetails,
    getDescription,
    onNotesChange,
    getWeight,
    weightUnit = 'lb',
    onWeightChange,
    canEditWeight,
    getValue,
    onValueChange,
    canEditValue,
    columnCount,
    mobileColumnCount,
    emptyMessage,
    getKey,
    getLabel,
    onSave,
    onDelete,
    canSave,
    isUnsavedItem,
    newItem,
}: TableInventoryProps<Item, SearchableItem>) {
    const [editingRows, setEditingRows] = useState([]);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const [originalItems, setOriginalItems] = useState<Record<string, Item>>({});
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 575.98px)').matches);
    useEffect(() => {
        const query = window.matchMedia('(max-width: 575.98px)');
        const update = () => setIsMobile(query.matches);
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);
    const visibleColumnCount = (isMobile ? mobileColumnCount ?? columnCount : columnCount) || 1;

    const displayedItems = useMemo(() => {
        if (!sorted) {
            return items;
        }

        return [...items].sort((a, b) =>
            getLabel(a).localeCompare(getLabel(b)),
        );
    }, [items, sorted, getLabel]);

    return (
        <>
            <div className={`table-responsive inventory-table ${className}`}>
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            {thead}

                            <th
                                scope="col"
                                className="text-end"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedItems.map((item) => {
                            const isEditing = editingRows.includes(getKey(item));

                            const itemKey = getKey(item);
                            const isExpanded = expandedRows.includes(itemKey);
                            const description = getDescription?.(item);
                            const notes = (item as Item & { notes?: string | null }).notes || '';
                            const hasDetails = Boolean(renderDetails || getDescription || onNotesChange || getWeight || getValue);
                            const canToggleDetails = hasDetails && !isEditing;
                            const toggleDetails = () => setExpandedRows((current) => current.includes(itemKey)
                                ? current.filter((key) => key !== itemKey)
                                : [...current, itemKey]);

                            return (
                                <Fragment key={itemKey}>
                                    <tr
                                        className={canToggleDetails ? 'inventory-item-row' : undefined}
                                        tabIndex={canToggleDetails ? 0 : undefined}
                                        aria-expanded={canToggleDetails ? isExpanded : undefined}
                                        aria-label={canToggleDetails ? `Details for ${getLabel(item)}` : undefined}
                                        onClick={(event) => {
                                            if (canToggleDetails && !(event.target as HTMLElement).closest('button, input, select, textarea, a, label')) {
                                                toggleDetails();
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (canToggleDetails && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                toggleDetails();
                                            }
                                        }}
                                    >
                                        {renderItem(item, isEditing)}

                                        <td>
                                            <div className="d-flex justify-content-end align-items-center gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-success"
                                                            disabled={canSave ? !canSave(item) : false}
                                                            onClick={async () => {
                                                                const saved = await onSave?.(item);
                                                                if (saved === false) return;
                                                                const itemKey = getKey(item);
                                                                setEditingRows(editingRows.filter((key) => key !== itemKey));
                                                                setOriginalItems((current) => {
                                                                    const { [itemKey]: _original, ...remaining } = current;
                                                                    return remaining;
                                                                });
                                                            }}
                                                        >
                                                            <i className="fa fa-check" aria-hidden="true"></i>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => {
                                                                const itemKey = getKey(item);
                                                                if (isUnsavedItem?.(item)) {
                                                                    setItems(items.filter((current) => getKey(current) !== getKey(item)));
                                                                } else if (originalItems[itemKey]) {
                                                                    setItems(items.map((current) => getKey(current) === itemKey ? originalItems[itemKey] : current));
                                                                }
                                                                setEditingRows(editingRows.filter((key) => key !== itemKey));
                                                                setOriginalItems((current) => {
                                                                    const { [itemKey]: _original, ...remaining } = current;
                                                                    return remaining;
                                                                });
                                                            }}
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {hasDetails && !isMobile && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${getLabel(item)}`}
                                                                aria-expanded={isExpanded}
                                                                onClick={toggleDetails}
                                                            >
                                                                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true"></i>
                                                            </button>
                                                        )}

                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => {
                                                                const itemKey = getKey(item);
                                                                setOriginalItems((current) => ({ ...current, [itemKey]: item }));
                                                                setEditingRows([...editingRows, itemKey]);
                                                            }}
                                                        >
                                                            <i className="fa fa-pencil" aria-hidden="true"></i>
                                                        </button>

                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={async () => {
                                                                const deleted = await onDelete?.(item);
                                                                if (deleted === false) return;
                                                                setItems(items.filter((i) => getKey(i) !== getKey(item)));
                                                            }}
                                                        >
                                                            <i className="fa fa-trash" aria-hidden="true"></i>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>

                                    {hasDetails && (isExpanded || isEditing) && (
                                        <tr>
                                            <td colSpan={visibleColumnCount + 1} className="bg-body-secondary">
                                                {description && <div className="mb-3">{description}</div>}
                                                {onNotesChange && (
                                                    <div className={renderDetails ? 'mb-2' : undefined}>
                                                        <strong>Notes</strong>
                                                        {isEditing ? <textarea value={notes} onChange={(event) => onNotesChange(item, event.target.value)} className="form-control fst-italic mt-1" aria-label={`Notes for ${getLabel(item)}`} /> : notes ? <p className="mb-0 fst-italic" style={{ whiteSpace: 'pre-wrap' }}>{notes}</p> : <p className="mb-0 text-body-secondary fst-italic">No notes.</p>}
                                                    </div>
                                                )}
                                                <hr />
                                                {renderDetails && (
                                                    <>
                                                        {renderDetails(item, isEditing)}
                                                        <hr />
                                                    </>
                                                )}
                                                {getValue && (
                                                    <div className="mb-2">
                                                        {isEditing && onValueChange && (!canEditValue || canEditValue(item)) ? (
                                                            <Currencies
                                                                {...getValue(item)}
                                                                onChange={(currency, amount) => onValueChange(item, { ...getValue(item), [currency]: amount })}
                                                                outline
                                                            />
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-coins" title="Value" aria-label="Value" />
                                                                &nbsp;
                                                                <CurrencyText {...getValue(item)} />
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {getWeight && (
                                                    <div>
                                                        <i className="fas fa-weight-hanging" title="Weight" aria-label="Weight" />
                                                        &nbsp;
                                                        {isEditing && onWeightChange && (!canEditWeight || canEditWeight(item)) ? <NumberInput min={0} step="any" value={getWeight(item)} onValueChange={(weight) => onWeightChange(item, weight)} className="form-control d-inline-block" style={{ width: 100 }} aria-label={`Weight of ${getLabel(item)}`} /> : <Weight value={getWeight(item)} unit={weightUnit} />}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}

                        {displayedItems.length === 0 && emptyMessage && (
                            <tr>
                                <td colSpan={visibleColumnCount + 1} className="text-center text-body-secondary">
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}

                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-end align-items-center gap-2">
                {showSearchableList && (
                    <SearchableList
                        items={searchableItems}
                        getKey={getKey}
                        getLabel={getLabel}
                        onSelect={onSelect}
                        buttonLabel="Add"
                        sorted={sortedSearchable}
                    />
                )}

                {newItem && (
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                            const item = newItem();
                            setItems([...items, item]);
                            const itemKey = getKey(item);
                            setOriginalItems((current) => ({ ...current, [itemKey]: item }));
                            setEditingRows([...editingRows, itemKey]);
                        }}
                    >
                        Add Custom
                    </Button>
                )}
            </div>
        </>
    );
}

export default TableInventory;
